import { useCallback, useState } from 'react'

export type CommandOptions<TData, TResponse> = {
    commandFn: (data: TData) => TResponse | Promise<TResponse>
    onFinish?: (result: CommandState<TResponse>) => void
}

type CommandState<TResponse> = {
    status: CommandStatus
    response: TResponse
    errorText: string
}

const useCommand = <TData, TResponse>(options: CommandOptions<TData, TResponse>) => {
    const executeCommand = useCallback(async (data: TData) => {
        console.log('executing usecallback')

        setQState(v => ({ ...v, status: 'loading' }))
        let response: TResponse = null
        let status: CommandStatus = 'loading'
        let errorText = ''

        try {
            response = await options.commandFn(data)
            status = 'success'
        } catch (error) {
            if (error instanceof Error) {
                status = 'error'
                console.log('Error occurred:', error)
                errorText = error.message ?? 'An error occurred.'
            } else {
                status = 'error'
                console.log('Unknown error occurred:', error)
                errorText = 'An unknown error occurred.'
            }
        }

        setQState(v => ({ ...v, status, response, errorText }))
        if (options.onFinish)
            options.onFinish({ ...qState, status, response, errorText })

        return response
    }, [options.commandFn, options.onFinish])

    const [qState, setQState] = useState<CommandState<TResponse>>({
        status: 'idle',
        response: null,
        errorText: ''
    })

    return { ...qState, execute: executeCommand }
}

export default useCommand

export type CommandStatus = 'idle' | 'loading' | 'success' | 'error'
